<script setup lang="ts">
import Confirmation, { type ConfirmDialogProps } from "primevue/confirmdialog";
import ButtonComponent from "@/components/ButtonComponent/ButtonComponent.vue";
import { PresetHelper } from "@/helpers/preset.helper";
import { IconName } from "@/constants";
import IconComponent from "@/components/IconComponent/IconComponent.vue";
import ErrorDisplayComponent from "../ErrorDisplayComponent/ErrorDisplayComponent.vue";

type TaiConfirmDialogSize = "small" | "large";
interface TaiConfirmDialogProps extends /* @vue-ignore */ ConfirmDialogProps {
  size?: TaiConfirmDialogSize;
  errorMessage?: string;
}

const props = defineProps<TaiConfirmDialogProps>();

const preset = new PresetHelper({
  classMap: {
    tai__confirmation_panel: {
      layout: ["p-6 max-h-[90dvh] flex flex-col"],
      color: ["!bg-white"],
      border: ["rounded-lg shadow-md"]
    },
    "tai__confirmation_panel--small": {
      layout: ["w-screen sm:max-w-[464px]"]
    },
    "tai__confirmation_panel--large": {
      layout: ["w-screen sm:max-w-[900px]"]
    },
    tai__confirmation_header_close_icon: {
      color: ["!text-system-grey"],
      hover: ["hover:cursor-pointer hover:opacity-70"]
    },
    tai__confirmation_header: {
      layout: ["flex items-center justify-between pb-6 gap-4"],
      typography: ["text-desktop-h2 font-semibold"],
      border: ["border-system-light-grey border-b-[1px]"]
    },
    tai__confirmation_header_title: {
      layout: ["flex items-center gap-4"]
    },
    tai__confirmation_header_title_icon: {
      color: ["text-contrast-bitter-sweet"]
    },
    tai__confirmation_content: {
      layout: ["flex-1 flex gap-3 py-6  overflow-y-scroll"],
      typography: ["text-body font-normal text-center"]
    },
    tai__confirmation_footer: {
      layout: ["flex justify-between w-full"]
    }
  },
  preset: {
    panel: (ctx: { attrs: TaiConfirmDialogProps }) => [
      "tai__confirmation_panel",
      {
        "tai__confirmation_panel--large": ctx.attrs.size === "large",
        "tai__confirmation_panel--small":
          !ctx.attrs.size || ctx.attrs.size === "small"
      }
    ],
    header: () => ["tai__confirmation_header"],
    headerTitle: () => ["tai__confirmation_header_title"],
    headerTitleIcon: () => ["tai__confirmation_header_title_icon"],
    headerCloseIcon: () => ["tai__confirmation_header_close_icon"],
    content: () => ["tai__confirmation_content"],
    footer: () => ["tai__confirmation_footer"]
  }
}).applyPreset({ attrs: props });
</script>

<template>
  <Confirmation v-bind="{ ...$props, ...$attrs }" unstyled>
    <template #container="{ message: config, acceptCallback, rejectCallback }">
      <div
        :class="preset.panel.class"
        data-testid="tai-confirmation-dialog-panel"
      >
        <header :class="preset.header.class">
          <div :class="preset.headerTitle.class">
            <span v-if="config.icon" :class="preset.headerTitleIcon.class">
              <IconComponent :name="config.icon" />
            </span>
            <h2 data-testid="tai-confirmation-dialog-header-title">
              {{ config.header || "Confirm Action" }}
            </h2>
          </div>
          <span :class="preset.headerCloseIcon.class">
            <!-- Slot to customize reject callback behavior -->
            <slot name="headerCloseIcon" :reject-callback="rejectCallback">
              <button @click="rejectCallback">
                <IconComponent :name="IconName.Close" />
              </button>
            </slot>
          </span>
        </header>
        <section :class="preset.content.class">
          <div class="flex flex-col gap-4 w-full">
            <slot name="errorDetails" v-if="config.errorDetails">
              <ErrorDisplayComponent
                variant="small"
                :message="
                  config.errorMessage ||
                  props.errorMessage ||
                  'An error occurred.'
                "
                :details="config.errorDetails"
              />
            </slot>
            <slot v-else name="message">
              <p
                data-testid="tai-confirmation-dialog-content-message"
                class="text-system-grey"
              >
                {{ config.message || "Are you sure you want to proceed?" }}
              </p>
            </slot>
          </div>
        </section>
        <footer :class="preset.footer.class">
          <!-- Slot to customize accept / reject callback behavior -->
          <slot
            name="footer"
            :config="config"
            :reject-callback="rejectCallback"
            :accept-callback="acceptCallback"
          >
            <ButtonComponent
              data-testid="tai-reject-button"
              :label="config.rejectLabel || 'Cancel'"
              @click="rejectCallback"
              variant="primary"
              outlined
              size="small"
            />
            <ButtonComponent
              data-testid="tai-accept-button"
              :label="config.acceptLabel || 'Confirm'"
              @click="acceptCallback"
              variant="primary"
              size="small"
            />
          </slot>
        </footer>
      </div>
    </template>
  </Confirmation>
</template>
